import { useCallback, useEffect, useRef, useState } from 'react';

import type { IssuedCanvasserOnboardingToken, OnboardingApi, OnboardingResult } from './onboardingApi';
import { isNonBlankString } from './onboardingValidation';

export const QR_REFRESH_INTERVAL_MS = 5_000;
export const QR_REFRESH_ERROR = 'We couldn’t refresh the QR code. The current code can still be used. Try again.';
export const QR_UNAVAILABLE_ERROR = 'We couldn’t create a QR code. Try again.';
export const QR_MISSING_BROKERAGE_ERROR =
  'Your account session is missing brokerage context. Sign out and sign in again, then retry.';

const PROGRESS_UPDATE_INTERVAL_MS = 100;
const MAX_TIMER_DELAY_MS = 2_147_483_647;

export type CanvasserOnboardingQrClock = {
  now: () => number;
  nowEpoch: () => number;
};

export type CanvasserOnboardingQrScheduler = {
  setTimeout: (callback: () => void, delayMs: number) => number;
  clearTimeout: (timerId: number) => void;
};

export type CanvasserOnboardingQrVisibilityTarget = {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener: (type: 'visibilitychange', listener: EventListener) => void;
  removeEventListener: (type: 'visibilitychange', listener: EventListener) => void;
};

export type CanvasserOnboardingQrState =
  | { status: 'loading' }
  | {
      status: 'ready';
      token: string;
      expiresAt: Date;
      cycleStartedAt: number;
      elapsedMs: number;
      refreshError?: typeof QR_REFRESH_ERROR;
      isRefreshing: boolean;
    }
  | {
      status: 'unavailable';
      message: typeof QR_UNAVAILABLE_ERROR | typeof QR_MISSING_BROKERAGE_ERROR;
      reason: 'request-failed' | 'missing-brokerage';
    };

export type UseCanvasserOnboardingQrOptions = {
  api: Pick<OnboardingApi, 'issueToken'>;
  brokerageId: string | null | undefined;
  clock?: CanvasserOnboardingQrClock;
  scheduler?: CanvasserOnboardingQrScheduler;
  visibilityTarget?: CanvasserOnboardingQrVisibilityTarget | null;
};

type ActiveQr = {
  token: string;
  expiresAt: Date;
  cycleStartedAt: number;
  refreshError?: typeof QR_REFRESH_ERROR;
};

const browserClock: CanvasserOnboardingQrClock = {
  now: () => (typeof performance === 'undefined' ? Date.now() : performance.now()),
  nowEpoch: () => Date.now(),
};

const browserScheduler: CanvasserOnboardingQrScheduler = {
  setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
  clearTimeout: timerId => window.clearTimeout(timerId),
};

const getVisibilityTarget = (): CanvasserOnboardingQrVisibilityTarget | null =>
  typeof document === 'undefined' ? null : document;

const clampElapsed = (cycleStartedAt: number, now: number): number =>
  Math.max(0, Math.min(QR_REFRESH_INTERVAL_MS, now - cycleStartedAt));

const isFutureExpiry = (expiresAt: Date, nowEpoch: number): boolean =>
  Number.isFinite(expiresAt.getTime()) && expiresAt.getTime() > nowEpoch;

const isUsableIssueResult = (
  result: OnboardingResult<IssuedCanvasserOnboardingToken>,
  nowEpoch: number,
): result is { kind: 'success'; value: IssuedCanvasserOnboardingToken } =>
  result.kind === 'success' &&
  isNonBlankString(result.value.token) &&
  result.value.expiresAt instanceof Date &&
  isFutureExpiry(result.value.expiresAt, nowEpoch);

const toReadyState = (qr: ActiveQr, elapsedMs: number, isRefreshing: boolean): CanvasserOnboardingQrState => ({
  status: 'ready',
  token: qr.token,
  expiresAt: qr.expiresAt,
  cycleStartedAt: qr.cycleStartedAt,
  elapsedMs,
  refreshError: qr.refreshError,
  isRefreshing,
});

export const useCanvasserOnboardingQr = ({
  api,
  brokerageId,
  clock = browserClock,
  scheduler = browserScheduler,
  visibilityTarget = getVisibilityTarget(),
}: UseCanvasserOnboardingQrOptions): { state: CanvasserOnboardingQrState; retry: () => void } => {
  const [state, setState] = useState<CanvasserOnboardingQrState>({ status: 'loading' });
  const retryRequestRef = useRef<() => void>(() => undefined);

  const retry = useCallback((): void => retryRequestRef.current(), []);

  useEffect(() => {
    let active = true;
    let visible = visibilityTarget === null || visibilityTarget.visibilityState === 'visible';
    let generation = 0;
    let inFlight = false;
    let requestController: AbortController | null = null;
    let issueTimer: number | null = null;
    let progressTimer: number | null = null;
    let expiryTimer: number | null = null;
    let currentQr: ActiveQr | null = null;

    const clearTimer = (timerId: number | null): null => {
      if (timerId !== null) scheduler.clearTimeout(timerId);
      return null;
    };

    const clearIssueTimer = (): void => {
      issueTimer = clearTimer(issueTimer);
    };

    const clearProgressTimer = (): void => {
      progressTimer = clearTimer(progressTimer);
    };

    const clearExpiryTimer = (): void => {
      expiryTimer = clearTimer(expiryTimer);
    };

    const clearTimers = (): void => {
      clearIssueTimer();
      clearProgressTimer();
      clearExpiryTimer();
    };

    const setUnavailable = (reason: 'request-failed' | 'missing-brokerage'): void => {
      currentQr = null;
      clearProgressTimer();
      clearExpiryTimer();
      setState({
        status: 'unavailable',
        message: reason === 'missing-brokerage' ? QR_MISSING_BROKERAGE_ERROR : QR_UNAVAILABLE_ERROR,
        reason,
      });
    };

    const scheduleProgressUpdate = (): void => {
      if (!active || !visible || inFlight || !currentQr) return;

      const elapsedMs = clampElapsed(currentQr.cycleStartedAt, clock.now());
      if (elapsedMs >= QR_REFRESH_INTERVAL_MS) {
        setState(toReadyState(currentQr, QR_REFRESH_INTERVAL_MS, false));
        return;
      }

      progressTimer = scheduler.setTimeout(() => {
        progressTimer = null;
        if (!active || !visible || inFlight || !currentQr) return;

        const nextElapsedMs = clampElapsed(currentQr.cycleStartedAt, clock.now());
        setState(toReadyState(currentQr, nextElapsedMs, false));
        scheduleProgressUpdate();
      }, Math.min(PROGRESS_UPDATE_INTERVAL_MS, QR_REFRESH_INTERVAL_MS - elapsedMs));
    };

    const scheduleExpiryCheck = (): void => {
      if (!currentQr) return;

      const delayMs = currentQr.expiresAt.getTime() - clock.nowEpoch();
      if (delayMs <= 0) {
        setUnavailable('request-failed');
        return;
      }

      expiryTimer = scheduler.setTimeout(() => {
        expiryTimer = null;
        if (!active || !currentQr) return;

        if (isFutureExpiry(currentQr.expiresAt, clock.nowEpoch())) {
          scheduleExpiryCheck();
          return;
        }

        setUnavailable('request-failed');
      }, Math.min(delayMs, MAX_TIMER_DELAY_MS));
    };

    const scheduleIssue = (delayMs: number): void => {
      clearIssueTimer();
      issueTimer = scheduler.setTimeout(() => {
        issueTimer = null;
        requestToken();
      }, delayMs);
    };

    const handleFailure = (): void => {
      inFlight = false;
      requestController = null;

      if (currentQr && isFutureExpiry(currentQr.expiresAt, clock.nowEpoch())) {
        currentQr.refreshError = QR_REFRESH_ERROR;
        setState(toReadyState(currentQr, QR_REFRESH_INTERVAL_MS, false));
        scheduleIssue(QR_REFRESH_INTERVAL_MS);
        return;
      }

      setUnavailable('request-failed');
    };

    const requestToken = (): void => {
      if (!active || !visible || inFlight) return;

      clearIssueTimer();
      clearProgressTimer();

      if (!isNonBlankString(brokerageId)) {
        setUnavailable('missing-brokerage');
        return;
      }

      inFlight = true;
      const requestGeneration = generation;
      const retainedQr = currentQr;

      if (retainedQr && isFutureExpiry(retainedQr.expiresAt, clock.nowEpoch())) {
        setState(toReadyState(retainedQr, QR_REFRESH_INTERVAL_MS, true));
      } else {
        currentQr = null;
        clearExpiryTimer();
        setState({ status: 'loading' });
      }

      requestController = typeof AbortController === 'undefined' ? null : new AbortController();
      const controller = requestController;

      let issueRequest: ReturnType<OnboardingApi['issueToken']>;
      try {
        issueRequest = api.issueToken({ id: brokerageId }, { signal: controller?.signal });
      } catch {
        issueRequest = Promise.reject();
      }

      void issueRequest
        .then(result => {
          if (!active || !visible || requestGeneration !== generation) return;

          if (!isUsableIssueResult(result, clock.nowEpoch())) {
            handleFailure();
            return;
          }

          inFlight = false;
          requestController = null;
          clearExpiryTimer();
          currentQr = {
            token: result.value.token,
            expiresAt: new Date(result.value.expiresAt.getTime()),
            cycleStartedAt: clock.now(),
          };
          setState(toReadyState(currentQr, 0, false));
          scheduleExpiryCheck();
          scheduleProgressUpdate();
          scheduleIssue(QR_REFRESH_INTERVAL_MS);
        })
        .catch(() => {
          if (!active || !visible || requestGeneration !== generation) return;
          handleFailure();
        });
    };

    retryRequestRef.current = requestToken;

    const handleVisibilityChange: EventListener = () => {
      const nextVisible = visibilityTarget === null || visibilityTarget.visibilityState === 'visible';
      if (nextVisible === visible) return;

      visible = nextVisible;
      generation += 1;
      clearTimers();

      if (!visible) {
        inFlight = false;
        requestController?.abort();
        requestController = null;
        return;
      }

      requestToken();
    };

    visibilityTarget?.addEventListener('visibilitychange', handleVisibilityChange);

    if (!isNonBlankString(brokerageId)) {
      setUnavailable('missing-brokerage');
    } else if (visible) {
      requestToken();
    }

    return () => {
      active = false;
      generation += 1;
      clearTimers();
      requestController?.abort();
      requestController = null;
      retryRequestRef.current = () => undefined;
      visibilityTarget?.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [api, brokerageId, clock, scheduler, visibilityTarget]);

  return { state, retry };
};
